import React from "react";
import useMaritimeEventForm from "../../../hooks/useMaritimeEventForm";
import PermitHeader from "./PermitHeader";
import { PermitType } from "../../../common/enums";
import DangerAlert from "../../Common/DangerAlert";
import PermitActionBar from "./PermitActionBar";
import DateTimeInput from "../../Common/DateTimeInput";
import RemindMe from "./RemindMe";
import TextInput from "../../Common/TextInput";
import TextArea from "../../Common/TextArea";
import Button from "../../Common/Button";
import SignatureModal from "../../Signatures/SignatureModal";
import RejectPermitModal from "../RejectPermitModal";
import SignatureDisplay from "../../Signatures/SignatureDisplay";
import LoadingSpinner from "../../Common/LoadingSpinner";
import AdminComment from "./AdminComment";
import { PermitFormProps } from "../../../types/forms/Permits/PermitFormProps";
import AcceptUnsavedChangesModal from "./AcceptUnsavedChangesModal";
import RadioButton from "../../Common/RadioButton";
import { MaritimeEventType } from "../../../common/enums";
import PositionPicker from "./PositionPicker";

function MaritimeEventForm(props: PermitFormProps) {
    const formManager = useMaritimeEventForm(props);

    const { formState, storedPermit, department, formStateChangeHandler } = formManager;
    const { resources } = props;

    if (formManager.isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <div className="mt-5 mb-5">
                <div className="container">
                    <div className="mb-5">
                        <PermitHeader
                            formState={formState}
                            permitDocument={storedPermit}
                            permitNameResource={resources.get("MaritimeEvent")}
                            permitType={PermitType.MaritimeEvent}
                            resources={resources}
                        />
                    </div>

                    {formManager.showRejectionComment ? (
                        <DangerAlert>{storedPermit?.rejectionComment ?? ""}</DangerAlert>
                    ) : null}

                    {storedPermit ? (
                        <PermitActionBar
                            acceptPermitClickHandler={formManager.acceptButtonClickHandler}
                            copyPermitClickHandler={formManager.copyButtonClickHandler}
                            rejectPermitClickHandler={formManager.rejectPermitClickHandler}
                            renewPermitClickHandler={formManager.renewPermitClickHandlerAsync}
                            department={department}
                            permitDocument={storedPermit}
                            resources={props.resources}
                            requestInProgress={formManager.requestInProgress}
                        />
                    ) : null}

                    <form onSubmit={formManager.submitHandler}>
                        <PositionPicker
                            canManagePositions={formManager.canManagePositions}
                            canManagePositionIsActive={formManager.canManagePositions}
                            canManagePolygons={formManager.canManagePositionPolygons}
                            canManageComments={formManager.canManagePositions}
                            onPositionAdded={formManager.addPositionPickerRowHandler}
                            onPositionChange={formManager.updatePositionPickerRowHandler}
                            onPositionRemoved={formManager.removePositionPickerRowHandler}
                            onUpdatePositionsClicked={formManager.updatePositionsClickHandlerAsync}
                            positions={formState.positions}
                            resources={props.resources}
                            showUpdatePositionsButton={formManager.showUpdatePositionsButton}
                            canManageCategories={false}
                            selectedCategories={[]}
                            showActiveStatus={true}
                            showCategories={false}
                        />

                        <div className="row mt-3">
                            <DateTimeInput
                                defaultValue={formState.startDate.value ?? new Date()}
                                id="start-date"
                                label={resources.get("StartDateTime")}
                                onBlur={formManager.startDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.startDate.errorMessage}
                                ref={formManager.startDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                            <DateTimeInput
                                defaultValue={formState.endDate.value ?? new Date()}
                                id="end-date"
                                label={resources.get("EndDateTime")}
                                onBlur={formManager.endDateHandler.blurHandler}
                                className="col-md-6"
                                errorMessage={formState.endDate.errorMessage}
                                minValue={formState.startDate.value}
                                ref={formManager.endDateHandler.ref}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="mt-3">
                            <RemindMe
                                formState={formState}
                                onChange={formManager.registerFormStateChange}
                                resources={resources}
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <fieldset className="row mt-3">
                            <legend style={{ fontSize: "16px", fontWeight: 700 }}>{resources.get("MaritimeEventType")}</legend>
                            <RadioButton
                                checked={formState.maritimeEventType.value === MaritimeEventType.General}
                                inputId="maritime-event-type__general"
                                label={resources.get("General")}
                                name="maritime-event-type__general"
                                onSelect={() => formStateChangeHandler("maritimeEventType", MaritimeEventType.General)}
                                readOnly={!formManager.canManageContent}
                            />
                            <RadioButton
                                checked={formState.maritimeEventType.value === MaritimeEventType.CraneService}
                                inputId="maritime-event-type__crane-service"
                                label={resources.get("CraneService")}
                                name="maritime-event-type__crane-service"
                                onSelect={() => formStateChangeHandler("maritimeEventType", MaritimeEventType.CraneService)}
                                readOnly={!formManager.canManageContent}
                            />
                            <RadioButton
                                checked={formState.maritimeEventType.value === MaritimeEventType.CraneArmLowered}
                                inputId="maritime-event-type__lowered-crane-arm"
                                label={resources.get("CraneArmLowered")}
                                name="maritime-event-type_lowered-crane-arm"
                                onSelect={() => formStateChangeHandler("maritimeEventType", MaritimeEventType.CraneArmLowered)}
                                readOnly={!formManager.canManageContent}
                            />
                        </fieldset>

                        <div className="row mt-3">
                            <TextInput
                                value={formState.entrepreneur.value}
                                errorMessage={formState.entrepreneur.errorMessage}
                                inputId="entrepreneur"
                                labelText={props.resources.get("Entrepreneur")}
                                onChange={(s) => formStateChangeHandler("entrepreneur", s)}
                                containerClassName="col-md-6"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextArea
                                errorMessage={formState.description.errorMessage}
                                value={formState.description.value}
                                labelText={props.resources.get("WorkPurposeDescription")}
                                onChange={(s) => formStateChangeHandler("description", s)}
                                textAreaId="description"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <div className="row mt-3">
                            <TextInput
                                inputId="work-leader__name"
                                containerClassName="col-md-6"
                                value={formState.contactPerson.name}
                                errorMessage={formState.contactPerson.nameErrorMessage}
                                labelText={resources.get("ContactPerson")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("contactPerson", {
                                        ...formState.contactPerson,
                                        name: s
                                    })
                                }
                                readOnly={!formManager.canManageContent}
                            />
                            <TextInput
                                inputId="work-leader__phone-number"
                                containerClassName="col-md-6"
                                value={formState.contactPerson.phoneNumber}
                                errorMessage={formState.contactPerson.phoneNumberErrorMessage}
                                labelText={resources.get("ContactPersonPhoneNumber")}
                                onChange={(s) =>
                                    formManager.personFormElementChangeHandler("contactPerson", {
                                        ...formState.contactPerson,
                                        phoneNumber: s
                                    })
                                }
                                placeHolder="e.g. +46705555555"
                                readOnly={!formManager.canManageContent}
                            />
                        </div>

                        <AdminComment
                            formElement={formState.adminComment}
                            onChange={(s) => formStateChangeHandler("adminComment", s)}
                            readonly={!formManager.canManageAdminComment}
                            onUpdateButtonClick={formManager.updateAdminCommentButtonClickHandlerAsync}
                            showUpdateButton={formManager.showUpdateAdminCommentButton}
                            resources={resources}
                            show={formManager.showAdminComment}
                            containerClassName="mt-3"
                        />

                        <SignatureDisplay
                            resources={resources}
                            signatureManager={formManager.permitProviderSignatureManager}
                            signatureLabel={resources.get("PermitProvider")}
                            containerClassName="mt-3 mb-4"
                        />

                        {formManager.canCreate ? (
                            <Button className="mt-3" type="submit" disabled={formManager.requestInProgress}>
                                {resources.get("Send")}
                            </Button>
                        ) : formManager.canUpdate ? (
                            <Button className="mt-3" type="submit" disabled={formManager.requestInProgress}>
                                {resources.get("Update")}
                            </Button>
                        ) : null}
                    </form>
                </div>
            </div>
            <SignatureModal
                resources={resources}
                SignatureManager={formManager.permitProviderSignatureManager}
                ref={formManager.permitProviderSignatureManager.signaturePadRef}
            />
            <RejectPermitModal
                onHide={() => formManager.setShowRejectPermitModal(false)}
                onReject={formManager.rejectPermitHandlerAsync}
                resources={resources}
                show={formManager.showRejectPermitModal}
            />
            <AcceptUnsavedChangesModal
                show={formManager.showAcceptUnsavedChangesModal}
                onHide={() => formManager.setShowAcceptUnsavedChangesModal(false)}
                onAccept={formManager.unsavedChangesModalAcceptHandlerAsync}
                onUpdate={formManager.unsavedChangesModalUpdateHandlerAsync}
                resources={resources}
            />
        </>
    );
}

export default MaritimeEventForm;
